'use client'

// ============================================================================
// СТЕК ОКОН.
// ----------------------------------------------------------------------------
// Окно документа, открытое из окна сделки, показывалось ПОЗАДИ него. Причина не
// в том, что кто-то забыл поднять слой, — наоборот, слой у всех окон один и тот
// же, жёстко записанный `z-[70]`. При равных слоях порядок решает разметка: чьё
// поддерево оказалось в `body` раньше, то и внизу. Открытое вторым окно вполне
// может оказаться первым в портале.
//
// ── ПОЧЕМУ НЕ ПРОСТО ПОДНЯТЬ ЧИСЛО ─────────────────────────────────────────
// Потому что это лечение симптома, и лечится оно ровно один раз. Дальше кто-то
// откроет третье окно из второго и поставит себе 80. Потом четвёртое — 90. По
// разметке проекта уже гуляет восемь разных значений слоя, от 40 до 100, и
// каждое новое выбиралось на глаз. Через год там будет 99999.
//
// Слой окна должен быть НЕ ЧИСЛОМ, А МЕСТОМ В СТОПКЕ. Тогда его не надо
// выбирать: открыл окно — оно наверху, закрыл — верхним стало предыдущее.
//
// ── ЧТО ЕЩЁ ЛОМАЛОСЬ ИЗ-ЗА ОТСУТСТВИЯ СТОПКИ ───────────────────────────────
// Escape слушают ВСЕ открытые окна сразу, каждое на `document`. Вызов
// `stopPropagation` здесь не помогает: он останавливает всплытие к предкам, а
// `document` предков не имеет — остальные обработчики на том же узле всё равно
// срабатывают. Значит, один Escape закрывал и верхнее окно, и то, из которого
// его открыли.
//
// Ловушка фокуса — та же беда: два окна одновременно ловят Tab, и фокус мечется
// между ними.
//
// Обе чинятся тем же знанием: слушать должно только ВЕРХНЕЕ окно.
//
// Устройство повторяет `lib/ui/scroll-lock.ts` — там счётчик открытых окон уже
// доказал, что работает; здесь к нему добавляется порядок.
// ============================================================================

import { useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react'

/** Слой, ниже которого живут шапка, боковое меню и плавающие панели действий. */
const BASE_Z = 70

/**
 * Шаг между окнами — ЕДИНИЦА, а не десятка.
 *
 * Выше окон в проекте уже кое-что стоит, и стоит осмысленно: всплывающие
 * подсказки на 80, меню действий и выбор тегов на 90, оповещения на 100.
 * Оповещение обязано быть видно поверх любого окна — иначе человек не узнает,
 * что его действие не прошло.
 *
 * С шагом в десять третье вложенное окно встало бы ровно на 100 и закрыло
 * оповещения собой. С шагом в единицу окна занимают 71, 72, 73… и до восьмидесяти
 * не доходят даже вдесятером — а внутри окна собственные слои считаются от него
 * же: подложка создаёт свой контекст наложения, и запас там не нужен.
 */
const STEP = 1

/**
 * Выше этого окна не поднимаются НИКОГДА.
 *
 * Восьмидесятый слой занят подсказками, девяностый — меню действий, сотый —
 * оповещениями. Оповещение обязано быть видно поверх любого окна: иначе человек
 * не узнает, что его действие не прошло, и повторит его.
 *
 * Потолок означает, что с девятого вложенного окна порядок между ними снова
 * решает разметка. Это осознанный размен: девяти окон друг в друге в системе
 * нет и не будет, а закрытое собой оповещение — беда, которая случается на
 * ровном месте. Лучше потерять порядок там, где его некому заметить, чем
 * спрятать сообщение об ошибке.
 */
const CEILING_Z = 79

let stack: number[] = []
let nextId = 1
/** Меняется при любом изменении стопки — по нему подписчики понимают, что пора перечитать. */
let version = 0

const listeners = new Set<() => void>()

function notify() {
  version += 1
  for (const l of listeners) l()
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}

const getVersion = () => version
// На сервере стопки нет вовсе: окна рисуются только в браузере.
const getServerVersion = () => 0

// ── Работа со стопкой без React ─────────────────────────────────────────────
// Вынесено отдельно, чтобы это можно было проверить тестом: React для проверки
// порядка окон не нужен, а поднимать его ради трёх чисел — лишнее.

/** Положить окно наверх. Возвращает его пропуск. */
export function pushLayer(): number {
  const id = nextId++
  stack.push(id)
  notify()
  return id
}

/** Снять окно. Порядок закрытия значения не имеет: убираем по пропуску. */
export function popLayer(id: number): void {
  const before = stack.length
  stack = stack.filter(x => x !== id)
  if (stack.length !== before) notify()
}

export interface ModalLayer {
  /** z-index этого окна. */
  z: number
  /** Верхнее ли оно сейчас. Escape и ловушку фокуса слушает только верхнее. */
  isTop: boolean
}

/** Где окно в стопке. Незнакомый пропуск — окно закрыто. */
export function layerOf(id: number): ModalLayer {
  const index = stack.indexOf(id)
  if (index < 0) return { z: BASE_Z, isTop: false }
  return {
    z: Math.min(BASE_Z + (index + 1) * STEP, CEILING_Z),
    isTop: index === stack.length - 1,
  }
}

/** Сколько окон открыто. Нужно тестам и отладке. */
export function stackDepth(): number {
  return stack.length
}

/**
 * Аварийный сброс — по образцу `releaseScrollLock`.
 *
 * Окно может размонтироваться без своего обработчика: например, при переходе по
 * ссылке изнутри окна. Тогда пропуск остаётся в стопке навсегда, и следующее
 * окно открывается слоем выше, чем нужно, а «верхним» считается призрак.
 */
export function releaseModalStack(): void {
  stack = []
  notify()
}

// ── Хук ─────────────────────────────────────────────────────────────────────

// Слой нужен ДО отрисовки: если посчитать его обычным эффектом, первый кадр
// вложенное окно покажется на слое соседа — то есть ровно так, как ломалось.
// На сервере `useLayoutEffect` ругается, поэтому там обычный.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect

/**
 * Место окна в стопке, пока `active`.
 *
 * @param active окно открыто
 */
export function useModalLayer(active: boolean): ModalLayer {
  const idRef = useRef(0)

  useSyncExternalStore(subscribe, getVersion, getServerVersion)

  useIsomorphicLayoutEffect(() => {
    if (!active) return
    const id = pushLayer()
    idRef.current = id
    return () => {
      popLayer(id)
      idRef.current = 0
    }
  }, [active])

  return layerOf(idRef.current)
}
